import { useEffect, useRef } from 'react'
import { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { secureTokenStore } from '@store'
import { api } from '@api'
import { useAuth } from './AuthProvider'

interface RefreshTokenResponse {
  accessToken: string
  refreshToken: string
}

const refreshTokens = async (currentRefreshToken: string): Promise<RefreshTokenResponse> => {
  const { default: axios } = await import('axios')
  const refreshApi = axios.create({
    baseURL: api.defaults.baseURL,
  })

  const { data } = await refreshApi.post<RefreshTokenResponse>(
    '/authentication/refresh-token',
    { refreshToken: currentRefreshToken },
  )

  await secureTokenStore.setTokens(data.accessToken, data.refreshToken)

  return data
}

export const SetupAxiosInterceptors = () => {
  const { signOut } = useAuth()
  const isRefreshing = useRef(false)
  const failedQueue = useRef<Array<{
    resolve: (value?: any) => void
    reject: (error?: any) => void
    config: InternalAxiosRequestConfig
  }>>([])

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use((config) => {
      if (
        config.url?.includes('/authentication/sign-in') ||
        config.url?.includes('/authentication/forgot-password') ||
        config.url?.includes('/authentication/reset-password') ||
        config.url?.includes('/authentication/refresh-token') ||
        config.url?.includes('/authentication/revoke-token')
      ) {
        return config
      }

      const token = secureTokenStore.getAccessToken()
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      return config
    })

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.current.forEach((prom) => {
        if (error) {
          prom.reject(error)
        } else {
          if (token) {
            prom.config.headers['Authorization'] = `Bearer ${token}`
          }
          api.request(prom.config).then(prom.resolve).catch(prom.reject)
        }
      })
      failedQueue.current = []
    }

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          if (originalRequest.url?.includes('/authentication/sign-in')) {
            return Promise.reject(error)
          }

          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject, config: originalRequest })
            })
          }

          originalRequest._retry = true
          isRefreshing.current = true

          const currentRefreshToken = secureTokenStore.getRefreshToken()

          if (!currentRefreshToken) {
            isRefreshing.current = false
            signOut()
            return Promise.reject(error)
          }

          try {
            const response = await refreshTokens(currentRefreshToken)

            originalRequest.headers['Authorization'] = `Bearer ${response.accessToken}`
            processQueue(null, response.accessToken)
            isRefreshing.current = false
            return api.request(originalRequest)
          } catch (refreshError) {
            isRefreshing.current = false
            processQueue(refreshError, null)
            signOut()
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      },
    )

    return () => {
      api.interceptors.request.eject(requestInterceptor)
      api.interceptors.response.eject(responseInterceptor)
    }
  }, [signOut])

  return null
}
